// Story player.
import { useState, useEffect, useRef } from "react";
import { Play, Pause, ChevronRight, RotateCcw, RotateCw, Download, Send, Check } from "lucide-react";
import { MagicSpinner } from "../../Onboarding.jsx";
import { useLang } from "../../i18n.jsx";
import { api } from "../api.js";
import { fmtDate } from "../constants.js";
import { EmojiAvatar, Stars } from "../emoji.jsx";
import { toFa } from "../format.js";
import { askReview, downloadToDevice, haptic } from "../host.js";
import { messengerName, onboardKey, platformInfo } from "../platform.js";
import { C, FONT } from "../../design-system.js";
import { AudioProgress } from "../ui.jsx";

// ── Story Player ──────────────────────────────────────────────────────────────
export function StoryPlayerScreen({ story, child, onBack }) {
  const { t, dir } = useLang();
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(story.duration_seconds || 420);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(true);
  const [audioError, setAudioError] = useState(null);
  // «ارسال به گفتگوی من» — where the messenger's bot can DM an audio message
  // (platform_info.supports_audio_send: Bale, Telegram, Soroush), the story is sent
  // into the parent's own chat instead of a download that bounces them out to a
  // browser (product owner, 2026-10-27). Eitaa keeps «دانلود».
  const canSendToChat = !!platformInfo().supports_audio_send;
  const [chatState, setChatState] = useState("");   // "" | "sending" | "sent" | error text
  const audioRef = useRef(null);

  // Fetch presigned audio URL on mount
  useEffect(() => {
    setAudioLoading(true);
    setAudioError(null);
    api(`/stories/${story.id}/play?child_id=${child.id}`)
      .then(data => {
        setAudioUrl(data.url);
        if (data.title) {} // title already available from story prop
      })
      .catch(e => setAudioError(e.message || t("خطا در بارگذاری صدا")))
      .finally(() => setAudioLoading(false));
  }, [story.id, child.id]);

  // Audio element event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setElapsed(Math.floor(audio.currentTime));
    const onLoadedMetadata = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(Math.floor(audio.duration));
      }
    };
    const onEnded = () => {
      setPlaying(false);
      // Mark as heard ONLY when playback actually finishes (not on open/pause).
      api(`/stories/${story.id}/listened?child_id=${child.id}`, { method: "POST" }).catch(() => {});
      // Ask what they thought — at the ONE honest moment for it: a whole story has
      // just played to the end, so the product has actually delivered something.
      // Capability-probed (Bale v1.4+ today, no-op elsewhere) and once per user
      // ever, so a family that listens twice a week is never asked twice.
      try {
        const key = onboardKey().replace("onboarded", "reviewed").replace("zoha_seen_onboarding", "zoha_reviewed");
        if (!localStorage.getItem(key) && askReview(4)) localStorage.setItem(key, "1");
      } catch (_) {}
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("ended", onEnded);
    };
  }, [audioUrl]);

  // Play/pause sync
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;
    if (playing) {
      audio.play().catch(() => setPlaying(false));
    } else {
      audio.pause();
    }
  }, [playing, audioUrl]);

  const progress = duration > 0 ? (elapsed / duration) * 100 : 0;
  const f = s => `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
  function skip(d) {
    const audio = audioRef.current;
    if (audio && audioUrl) {
      audio.currentTime = Math.max(0, Math.min(audio.duration || duration, audio.currentTime + d));
    }
    setElapsed(e => Math.max(0, Math.min(duration, e + d)));
  }
  function seekTo(fr) {
    const audio = audioRef.current;
    const dur = (audio && isFinite(audio.duration) && audio.duration) ? audio.duration : duration;
    const t = Math.max(0, Math.min(dur, fr * dur));
    if (audio && audioUrl) audio.currentTime = t;
    setElapsed(Math.floor(t));
  }
  function download() {
    if (!audioUrl) return;
    // ?dl=1 makes the server send `Content-Disposition: attachment` (save, don't stream);
    // &fn=<title> sets the saved FILENAME to the story name (server builds filename*=UTF-8).
    // In-app messenger WebViews block downloads, so hand the URL to the host bridge → it
    // opens in the EXTERNAL browser, where the download runs.
    const fn = encodeURIComponent(`${(story.title || "قصه").replace(/[\\/:*?"<>|]/g, "").trim()}.mp3`);
    const sep = audioUrl.includes("?") ? "&" : "?";
    const dlUrl = `${audioUrl}${sep}dl=1&fn=${fn}`;
    // downloadToDevice picks the best route this host has: a NATIVE save via
    // downloadFile where it exists, else the device browser, else the old
    // open-in-browser workaround. Never an <a download> — that does nothing here.
    haptic("light");
    downloadToDevice(dlUrl, decodeURIComponent(fn));
  }

  async function sendToChat() {
    if (chatState === "sending") return;
    haptic("light");
    setChatState("sending");
    try {
      await api(`/stories/${story.id}/send-to-chat?child_id=${child.id}`, { method: "POST" });
      haptic("success");
      setChatState("sent");
    } catch (e) {
      setChatState(e.message || t("ارسال ناموفق بود. دوباره امتحان کنید."));
    }
  }

  const storyDate = story.assigned_at ? fmtDate(story.assigned_at) : "";

  return (
    <div style={{ minHeight: "100vh", background: C.bgDeep, fontFamily: FONT, display: "flex", flexDirection: "column", position: "relative", overflow: "hidden" }}>
      <Stars count={50} />
      <div style={{ position: "absolute", top: "30%", left: "50%", transform: "translate(-50%,-50%)", width: 300, height: 300, borderRadius: "50%", background: `radial-gradient(circle, ${C.gold}15 0%, transparent 70%)`, pointerEvents: "none" }} />

      {/* Hidden audio element */}
      {audioUrl && <audio ref={audioRef} src={audioUrl} preload="auto" />}

      <div style={{ position: "relative", padding: "20px 20px 0" }}>
        <button onClick={onBack} className="press" style={{ background: "none", border: "none", color: C.gold, fontSize: 16, cursor: "pointer", fontFamily: FONT, padding: 0, display: "flex", alignItems: "center", gap: 4 }}>
          <ChevronRight size={18} style={{ transform: dir === "ltr" ? "rotate(180deg)" : "none" }} /> {t("بازگشت")}
        </button>
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px 28px", position: "relative" }}>
        <div style={{ width: 190, height: 190, borderRadius: "50%", background: `radial-gradient(circle at 35% 35%, ${C.gold}33, ${C.goldDim}11)`, border: `2px solid ${C.gold}33`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 32, boxShadow: `0 0 60px ${C.gold}22`, position: "relative" }}>
          <EmojiAvatar size={76} style={{ animation: playing ? "breathe 3s ease-in-out infinite" : "none" }}>{child.avatar}</EmojiAvatar>
          {playing && <div style={{ position: "absolute", inset: -8, borderRadius: "50%", border: `2px solid ${C.gold}44`, animation: "pulse 2s ease-in-out infinite" }} />}
        </div>

        <div style={{ color: C.textMid, fontSize: 15, marginBottom: 8 }}>{t("{name} عزیز 🌙", { name: child.name })}</div>
        <h2 style={{ color: C.cream, fontSize: 22, fontWeight: 800, margin: "0 0 6px", textAlign: "center" }}>{story.title}</h2>
        <div style={{ color: C.goldDim, fontSize: 15, marginBottom: 40 }}>{storyDate}</div>

        {audioLoading && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, color: C.textMid, fontSize: 14, marginBottom: 20 }}>
            <MagicSpinner size={26} /> {t("در حال بارگذاری صدا...")}
          </div>
        )}
        {audioError && (
          <div style={{ color: C.red, fontSize: 14, marginBottom: 20 }}>{audioError}</div>
        )}

        <div style={{ width: "100%", marginBottom: 14 }}>
          <AudioProgress pct={progress} onSeekFrac={seekTo} width="100%" style={{ marginBottom: 6 }} />
          {/* direction:ltr → elapsed on the LEFT, total on the RIGHT (media-player convention). */}
          <div style={{ display: "flex", justifyContent: "space-between", direction: "ltr" }}>
            <span style={{ color: C.textMid, fontSize: 14 }}>{f(elapsed)}</span>
            <span style={{ color: C.textDim, fontSize: 14 }}>{f(duration)}</span>
          </div>
        </div>

        {/* direction:ltr → rewind (−۱۵) on the LEFT, forward (+۱۵) on the RIGHT. */}
        <div style={{ display: "flex", alignItems: "center", gap: 24, direction: "ltr" }}>
          <button onClick={() => skip(-15)} className="press"
            style={{ background: C.bgCard, border: `1px solid ${C.border}`, color: C.textMid, borderRadius: 14, width: 58, height: 58, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", gap: 1, position: "relative" }}>
            <RotateCcw size={20} />
            <span style={{ fontSize: 11, color: C.textDim, fontWeight: 700 }}>{toFa(15)}</span>
          </button>

          <button onClick={() => !audioLoading && audioUrl && setPlaying(!playing)} className="press playBtn"
            style={{ width: 76, height: 76, borderRadius: "50%", background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, border: "none", color: C.bgDeep, cursor: audioUrl ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 24px ${C.gold}55`, opacity: audioUrl ? 1 : 0.5 }}>
            {playing ? <Pause size={32} fill={C.bgDeep} /> : <Play size={32} fill={C.bgDeep} style={{ marginRight: -4 }} />}
          </button>

          <button onClick={() => skip(15)} className="press"
            style={{ background: C.bgCard, border: `1px solid ${C.border}`, color: C.textMid, borderRadius: 14, width: 58, height: 58, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", gap: 1, position: "relative" }}>
            <RotateCw size={20} />
            <span style={{ fontSize: 11, color: C.textDim, fontWeight: 700 }}>{toFa(15)}</span>
          </button>
        </div>

        {/* Send the story into the parent's own chat with the bot, as an audio
            message — it plays and saves there, no browser. */}
        {canSendToChat && (
          <div style={{ marginTop: 24, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, maxWidth: 300 }}>
            <button onClick={sendToChat} disabled={chatState === "sending" || chatState === "sent"} className="press"
              style={{ background: chatState === "sent" ? `${C.teal}18` : C.bgCard, border: `1px solid ${chatState === "sent" ? C.teal : C.borderGold}`, color: chatState === "sent" ? C.teal : C.gold, borderRadius: 14, padding: "11px 18px", fontFamily: FONT, fontSize: 14, fontWeight: 700, cursor: chatState === "sending" || chatState === "sent" ? "default" : "pointer", display: "flex", alignItems: "center", gap: 8 }}>
              {chatState === "sending" ? <><MagicSpinner size={18} /> {t("در حال ارسال…")}</>
                : chatState === "sent" ? <><Check size={17} /> {t("در گفتگوی شما با ربات است")}</>
                : <><Send size={17} /> {t("ارسال به گفتگوی من")}</>}
            </button>
            <span style={{ color: chatState && chatState !== "sending" && chatState !== "sent" ? C.red : C.textDim, fontSize: 12, lineHeight: 1.7, textAlign: "center" }}>
              {chatState && chatState !== "sending" && chatState !== "sent"
                ? chatState
                : t("قصه به‌صورت فایل صوتی در گفتگوی {app} با ربات ضحا قصه می‌آید؛ همان‌جا پخش و ذخیره می‌شود.", { app: messengerName() })}
            </span>
          </div>
        )}

        {/* Download — icon only (kept low-key by design). Only where the bot can't
            send an audio message (Eitaa): native downloadFile, else the browser. */}
        {audioUrl && !canSendToChat && (
          <button onClick={download} className="press" title={t("دانلود")} aria-label={t("دانلود")}
            style={{ marginTop: 24, background: C.bgCard, border: `1px solid ${C.border}`, color: C.textMid, borderRadius: "50%", width: 46, height: 46, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Download size={19} />
          </button>
        )}
      </div>
    </div>
  );
}

